<template>
    <div class="goals-summary">
        <h3>Goals</h3>
        <div class="goals-by-type">
            <div v-for="(goals, typeId) in groupedGoals" 
                 :key="typeId" 
                 class="goal-type-section"
            >
                <div class="goal-type-pill" :class="`type-${typeId}`">
                    {{ goalTypes[typeId] }}
                </div>
                <div class="goal-circles">
                    <div v-for="goal in goals" 
                         :key="goal.id" 
                         class="goal-progress"
                         @mouseenter="showTooltip(goal)"
                         @mouseleave="hideTooltip"
                         @click="handleGoalClick(goal)"
                         style="cursor: pointer"
                    >
                        <GoalProgressCircle
                            :progress="goal.progress"
                            :days-remaining="getDaysRemaining(goal.target_date)"
                            :color="getProgressColor(goal.goal_type_id)"
                        />

                        <!-- Tooltip -->
                        <div v-if="activeTooltip === goal.id" class="goal-tooltip">
                            <h4>{{ goal.title }}</h4>
                            <p>{{ goal.description }}</p>
                            <div class="tooltip-info">
                                <div class="tooltip-target">
                                    Target: {{ formatNumber(goal.target_value) }}
                                </div>
                                <div class="tooltip-progress">
                                    Completion: {{ Math.round(goal.progress) }}%
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { goalService } from '../services/goalService';
import { differenceInDays } from 'date-fns';
import GoalProgressCircle from './GoalProgressCircle.vue';

export default {
    name: 'GoalsSummary',
    components: {
        GoalProgressCircle
    },
    props: {
        goals: {
            type: Array,
            required: true
        }
    },
    
    emits: ['goalClick'],
    
    setup(props, { emit }) {
        const goalTypes = ref({});
        const activeTooltip = ref(null);

        const groupedGoals = computed(() => props.goals.reduce((acc, goal) => {
            if (!acc[goal.goal_type_id]) {
                acc[goal.goal_type_id] = [];
            }
            acc[goal.goal_type_id].push(goal);
            return acc;
        }, {}));

        const loadGoalTypes = async () => {
            try {
                const types = await goalService.getAllGoalTypes();
                goalTypes.value = types.reduce((acc, type) => {
                    acc[type.id] = type.name;
                    return acc;
                }, {});
            } catch (error) {
                console.error('Error loading goal types:', error);
            }
        };

        const getDaysRemaining = (targetDate) => {
            return differenceInDays(new Date(targetDate), new Date());
        };

        const getProgressColor = (typeId) => {
            switch (typeId) {
                case 1: return '#4285f4';  // Company/Work - Blue
                case 2: return '#34a853';  // Health - Green
                case 3: return '#fbbc05';  // Friends - Yellow
                case 4: return '#ea4335';  // Community - Red
                default: return '#666666'; // Default grey
            }
        };

        const showTooltip = (goal) => {
            activeTooltip.value = goal.id;
        };

        const hideTooltip = () => {
            activeTooltip.value = null;
        };

        const handleGoalClick = (goal) => {
            emit('goalClick', goal.id);
        };

        const formatNumber = (number) => {
            return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        };

        onMounted(loadGoalTypes);

        return {
            groupedGoals,
            goalTypes,
            getDaysRemaining,
            getProgressColor,
            activeTooltip,
            showTooltip,
            hideTooltip,
            handleGoalClick,
            formatNumber
        };
    }
};
</script>

<style scoped>
.goals-summary {
    background: white;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.goals-by-type {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.goal-type-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.goal-type-pill {
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 0.9rem;
    display: inline-block;
    align-self: flex-start;
}

.goal-circles {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 4px;
}

.goal-progress {
    position: relative;
    cursor: pointer;
}

.progress-ring__circle {
    transition: stroke-dashoffset 0.35s;
    transform: rotate(-90deg);
    transform-origin: 50% 50%;
    stroke-linecap: round;
}

.progress-ring__text {
    font-size: 14px;
    font-weight: bold;
    fill: #2c3e50;
}

.progress-ring__subtext {
    font-size: 10px;
    fill: #666;
}

.goal-tooltip {
    position: absolute;
    bottom: calc(100% + 10px);
    left: 50%;
    transform: translateX(-50%);
    background: white;
    padding: 12px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    width: 200px;
    z-index: 1000;
}

.goal-tooltip::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid white;
}

.goal-tooltip h4 {
    margin: 0 0 8px 0;
    color: #2c3e50;
}

.goal-tooltip p {
    margin: 0 0 8px 0;
    font-size: 0.9rem;
    color: #666;
}

.tooltip-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
}

.tooltip-target,
.tooltip-progress {
    font-size: 0.9rem;
    color: #2196f3;
    font-weight: 500;
}

/* Goal type colors */
.type-1 { background: #e3f2fd; color: #1976d2; }
.type-2 { background: #e8f5e9; color: #2e7d32; }
.type-3 { background: #fff3e0; color: #f57c00; }
.type-4 { background: #fbe9e7; color: #d32f2f; }
</style> 